import React, { useEffect, useState, memo, useRef } from 'react';
import styles from '../../product/components/styles.module.css';
import { connect } from 'react-redux';
import { setBranchId } from '../../../auth/actions';
import { Button, Toggle, Loading, Dropdown, Input } from '../../../../common/ui';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import { findGeneralSettings, updateGeneralSettings } from '../actions';
import Axios from '../../../../common/util/Axios';

const GeneralSettings = memo(
  ({
    setBranchId,
    auth: { branchList, branchId, companyId },
    productSettingsState: { productSettingsData },
    showAlert,
    findGeneralSettings,
    updateGeneralSettings,
  }) => {
    const originalDataRef = useRef({});
    const [generalSettingsForm, setGeneralSettingsForm] = useState({
      'is-price-editable': false,
      'is-product-image-display-enabled': false,
      'is-enable-online-credit-handling': false,
      'single-enter-confirm-enabled': false,
      'is-change-popup-open-after-payment-enabled': false,
      'is-hire-purchase-enabled': false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [verticals, setVerticals] = useState([]);
    const [verticalForm, setVerticalForm] = useState({ code: '', name: '' });
    const navigate = useNavigate();

    const loadVerticals = async () => {
      if (!companyId) return;
      try {
        const res = await Axios.get('/api/dashboard/settings/business-verticals', {
          params: { companyId },
        });
        setVerticals(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (error) {
        setVerticals([]);
      }
    };

    useEffect(() => {
      if (branchId) {
        findGeneralSettings();
      }
    }, [branchId]);

    useEffect(() => {
      loadVerticals();
    }, [companyId]);

    useEffect(() => {
      if (!Array.isArray(productSettingsData)) {
        return;
      }

      const nextForm = {
        'is-price-editable': '',
        'is-product-image-display-enabled': '',
        'is-enable-online-credit-handling': '',
        'single-enter-confirm-enabled': '',
        'is-change-popup-open-after-payment-enabled': '',
        'is-hire-purchase-enabled': '',
      };

      const getScopedValue = (paramKey) => {
        const scopedItems = productSettingsData.filter(
          (item) => item?.param === paramKey
        );
        const branchScoped = scopedItems.find(
          (item) => String(item?.branchId ?? '') === String(branchId ?? '')
        );
        if (branchScoped) {
          return branchScoped?.value ?? '';
        }
        const companyScoped = scopedItems.find((item) => item?.branchId == null);
        return companyScoped?.value ?? '';
      };

      Object.keys(nextForm).forEach((paramKey) => {
        nextForm[paramKey] = getScopedValue(paramKey);
      });

      originalDataRef.current = { ...nextForm };
      setGeneralSettingsForm(nextForm);
    }, [productSettingsData, branchId]);

    const handleToggleChange = (e) => {
      const { name, checked } = e.target;
      setGeneralSettingsForm((prev) => ({
        ...prev,
        [name]: checked ? 'true' : 'false',
      }));
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      const changedFields = {};
      Object.keys(generalSettingsForm).forEach((key) => {
        const currentVal = generalSettingsForm[key] ?? '';
        const originalVal = originalDataRef.current[key] ?? '';
        if (currentVal !== originalVal) {
          changedFields[key] = currentVal;
        }
      });

      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        setIsLoading(false);
        return;
      }

      const generalSettings = {
        generalSettingsData: changedFields,
      };
      const result = await updateGeneralSettings(generalSettings);
      if (result) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'General settings updated successfully!',
        });
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Failed to update general settings!',
        });
      }
      setIsLoading(false);
      findGeneralSettings();
    };

    const onClickCancel = () => {
      navigate('/');
    };

    const handleChangeBranch = (value) => {
      setBranchId(value);
    };

    const branchOptions = (branchList || []).map((b) => ({
      value: b.id,
      label: b.name,
    }));

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>General Settings</h1>
            <p className={styles.breadcrumb}>Settings / General</p>
          </div>
          {branchList && branchList.length > 0 && branchId && (
            <div className={styles.branchSelectContainer}>
              <Dropdown
                id="branch-select"
                label="Branch"
                value={branchId}
                onChange={handleChangeBranch}
                options={branchOptions}
                placeholder="Select branch"
              />
            </div>
          )}
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            {isLoading && <Loading variant="overlay" label="" />}
            <form onSubmit={onSubmit} noValidate>
              <div className={styles.topDiv}>
                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>General Cashier Settings</h2>
                  <p className={styles.topicCaption}>
                    Here you can change general cashier settings
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={generalSettingsForm['is-price-editable'] === 'true'}
                    onChange={handleToggleChange}
                    name="is-price-editable"
                    label="Price Editable access in cashier"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={
                      generalSettingsForm['is-product-image-display-enabled'] ===
                      'true'
                    }
                    onChange={handleToggleChange}
                    name="is-product-image-display-enabled"
                    label="Enable product image display"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={
                      generalSettingsForm['is-enable-online-credit-handling'] ===
                      'true'
                    }
                    onChange={handleToggleChange}
                    name="is-enable-online-credit-handling"
                    label="Online credit handling enable"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={generalSettingsForm['single-enter-confirm-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    name="single-enter-confirm-enabled"
                    label="Single Enter bill payment Enabled for cashier"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={generalSettingsForm['is-change-popup-open-after-payment-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    name="is-change-popup-open-after-payment-enabled"
                    label="Change popup open after payment Enabled for cashier"
                    labelPlacement="start"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={generalSettingsForm['is-hire-purchase-enabled'] === 'true'}
                    onChange={handleToggleChange}
                    name="is-hire-purchase-enabled"
                    label="Enable hire purchase configuration for cashier"
                    labelPlacement="start"
                  />
                </div>
              </div>

              <div className={styles.buttonPanelContainer}>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={onClickCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
          <div className={styles.formCard}>
            <div className={styles.topDiv}>
              <div className={styles.topicDiv}>
                <h2 className={styles.topicLabel}>Business Vertical Settings</h2>
                <p className={styles.topicCaption}>
                  Create a vertical for a separate set of bills. Receipt header text is edited in Receipt Settings.
                </p>
              </div>
              <div className={styles.rowDiv}>
                <Input
                  id="vertical-code"
                  label="Code"
                  value={verticalForm.code}
                  onInput={(event) => setVerticalForm((prev) => ({ ...prev, code: event.target.value }))}
                />
                <Input
                  id="vertical-name"
                  label="Name"
                  value={verticalForm.name}
                  onInput={(event) => setVerticalForm((prev) => ({ ...prev, name: event.target.value }))}
                />
                <Button
                  type="button"
                  onClick={async () => {
                    const result = await Axios.post(
                      '/api/dashboard/settings/create-business-vertical',
                      verticalForm,
                      { params: { companyId, branchId } }
                    ).catch((error) => error.response);
                    if (result?.data?.success) {
                      setVerticalForm({ code: '', name: '' });
                      showAlert({
                        isAlertOpen: true,
                        alertType: 'success',
                        alertMessage: 'Business vertical created',
                      });
                      loadVerticals();
                    } else {
                      showAlert({
                        isAlertOpen: true,
                        alertType: 'danger',
                        alertMessage: result?.data?.message || 'Could not create vertical',
                      });
                    }
                  }}
                >
                  Add vertical
                </Button>
              </div>
              {verticals.map((row) => (
                <div className={styles.rowDiv} key={row.id}>
                  <span>{row.name} ({row.code})</span>
                  <Toggle
                    checked={row.isActive === true || row.isActive === 1}
                    name={`vertical-${row.id}`}
                    label="Active"
                    labelPlacement="start"
                    onChange={async (event) => {
                      await Axios.post(
                        '/api/dashboard/settings/update-business-vertical',
                        { id: row.id, name: row.name, isActive: event.target.checked },
                        { params: { companyId, branchId } }
                      );
                      loadVerticals();
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  auth: state.authState,
  productSettingsState: state.productSettingsState,
});

export default connect(mapStateToProps, {
  setBranchId,
  showAlert,
  updateGeneralSettings,
  findGeneralSettings,
})(GeneralSettings);
